import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, Phone, Mail, MapPin } from "lucide-react";
import { serviceGroups, groupPath } from "../data/services";
import { legal, has, officeOneLine } from "../data/legal";

const socialIcons = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
};

export default function Footer() {
  const address = officeOneLine();
  const year = new Date().getFullYear();

  return (
    <footer className="border-t-4 border-acGold bg-acNavyDeep text-white/80">
      <div className="container-page grid gap-10 py-14 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <span className="text-lg font-bold text-white">
            {legal.brandName}
          </span>
          <p className="mt-4 text-sm">
            We are a migration support and coordination service. We prepare your
            documentation, match you with a registered migration agent, and stay
            on the matter to keep it moving.
          </p>
          <div className="mt-4 flex gap-3">
            {Object.entries(legal.social).map(([key, url]) => {
              if (!has(url)) return null;
              const Icon = socialIcons[key];
              return (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="grid h-9 w-9 place-items-center rounded-full bg-white/10 hover:bg-white/20"
                  aria-label={key}
                >
                  <Icon className="h-4 w-4" />
                </a>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-acGold">
            Pathways we support
          </h4>
          <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {serviceGroups.map((g) => (
              <Link
                key={g.slug}
                to={groupPath(g)}
                className="transition hover:text-acGold"
              >
                {g.title}
              </Link>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wide text-acGold">
            Contact
          </h4>
          <ul className="mt-4 space-y-3 text-sm">
            {has(legal.phone) && (
              <li className="flex items-start gap-2">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-acGold" />
                <a
                  href={`tel:${legal.phone}`}
                  className="transition hover:text-acGold"
                >
                  {legal.phone}
                </a>
              </li>
            )}
            {has(legal.email) && (
              <li className="flex items-start gap-2">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-acGold" />
                <a
                  href={`mailto:${legal.email}`}
                  className="transition hover:text-acGold"
                >
                  {legal.email}
                </a>
              </li>
            )}
            {has(address) && (
              <li className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-acGold" />
                <span>{address}</span>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-page py-6 text-xs leading-relaxed text-white/60">
          <p>
            Visa decisions are made solely by the Department of Home Affairs. No
            outcome is guaranteed. Information on this site is general in nature
            and is not migration advice.
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span>
              © {year} {legal.brandName}.
            </span>
            <span className="flex gap-4">
              <Link
                to="/privacy-policy/"
                className="transition hover:text-acGold"
              >
                Privacy
              </Link>
              <Link
                to="/terms-of-service/"
                className="transition hover:text-acGold"
              >
                Terms
              </Link>
              <Link to="/disclaimer/" className="transition hover:text-acGold">
                Disclaimer
              </Link>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
